import { getPack, type SessionView } from "@gsignatures/core";
import { useRef } from "react";
import type { SignerApi } from "../api/types";
import { Icon } from "../components/Icon";
import { isoDayLong, who, whenWithZone } from "../lib/format";
import { useFocusHeading } from "../lib/hooks";
import { formOf, lettersOf, uploadsOf } from "../state/flow";
import { S } from "../strings";

/** The finish line: what was signed, the copies, and what happens next. */
export function DoneScreen({ api, session }: { api: SignerApi; session: SessionView }) {
  const heading = useFocusHeading();
  const w = who(session.sender);
  const letters = lettersOf(session);
  const uploads = uploadsOf(session);
  const form = formOf(session);
  const pack = form?.pack ? getPack(form.pack) : null;
  const times = Object.values(session.progress.signed).sort();
  const last = times[times.length - 1];
  const formSignedAt = form ? session.progress.signed[form.id] : undefined;
  const formTitle = pack?.title ?? form?.title ?? "";
  // Signed by them; the company countersigns next — the copies come by email after that.
  const waiting = session.envelope.status === "signed";
  const links = useRef<(HTMLAnchorElement | null)[]>([]);

  const rows = [
    ...letters.map((letter) => ({ id: letter.id, title: letter.title, sub: S.done.letterSub(w), form: false })),
    form && {
      id: form.id,
      title: formTitle,
      sub: pack && formSignedAt ? S.done.formSub(isoDayLong(pack.validUntil(new Date(formSignedAt)))) : "",
      form: true,
    },
    ...uploads.map((u) => ({ id: u.id, title: u.title, sub: S.done.uploadSub(u.files.length), form: false })),
  ].filter((r): r is { id: string; title: string; sub: string; form: boolean } => !!r);

  const downloadAll = () => {
    links.current.forEach((a, i) => {
      if (a) setTimeout(() => a.click(), i * 400);
    });
  };

  return (
    <main className="page" id="main">
      <div className="panel panel-pad done-panel">
        <div className="stack">
          <div className="done-hero">
            <div className="done-check" aria-hidden="true">
              <Icon n="check" />
            </div>
            <h1 className="title" ref={heading} tabIndex={-1}>
              {S.done.title(session.signer.firstName)}
            </h1>
            {last && <p className="lede">{S.done.lede(whenWithZone(last), w)}</p>}
          </div>
          {waiting && (
            <div className="note">
              <Icon n="pen" />
              <span>{S.done.countersignNext(w)}</span>
            </div>
          )}
          <ul className="doc-list" hidden={waiting}>
            {rows.map((r, i) => (
              <li className="doc-row" key={r.id}>
                <div className={`thumb ${r.form ? "form" : ""}`} aria-hidden="true" />
                <div>
                  <b>{r.title}</b>
                  {r.sub && <div className="muted small">{r.sub}</div>}
                </div>
                <a
                  ref={(el) => {
                    links.current[i] = el;
                  }}
                  className="btn btn-quiet"
                  href={api.downloadUrl(r.id)}
                  download
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${S.done.download} ${r.title}`}
                >
                  <Icon n="download" /> {S.done.download}
                </a>
              </li>
            ))}
          </ul>
          {rows.length > 1 && !waiting && (
            <button type="button" className="btn btn-primary" onClick={downloadAll}>
              <Icon n="download" /> {S.done.downloadAll}
            </button>
          )}
          <p className="help center">{S.done.certNote}</p>
          {form && (
            <div className="note">
              <Icon n="mail" />
              <span>{S.done.emailNote(w, formTitle)}</span>
            </div>
          )}
          <div>
            <h2 className="subhead">{S.done.nextTitle}</h2>
            <ol className="steps-next">
              {form && <li>{S.done.next1(w, formTitle)}</li>}
              <li>{S.done.next2(w)}</li>
            </ol>
          </div>
          <p className="trust">
            <Icon n="shield" /> {S.done.trust}
          </p>
        </div>
      </div>
    </main>
  );
}
